import { Building2, ExternalLink, MapPin } from "lucide-react";
import { Link } from "react-router";
import { regionToSlug } from "~/lib/directory-utils";
import { formatAmountWithCurrency, formatDate } from "~/lib/format";
import type { CompanyProfile } from "~/types/company";
import { DataRow, OverviewCard } from "./card";

function mapyCzUrl(address: string): string {
	return `https://mapy.cz/zakladni?q=${encodeURIComponent(address)}`;
}

/** ③ Základní údaje + Sídlo — registry facts and the registered address with a map link. */
export function BasicInfoCard({ company }: { company: CompanyProfile }) {
	const regionSlug = company.region ? regionToSlug(company.region) : null;
	const fileNumber = company.courtFileNumber
		? [company.courtFileNumber, company.registrationCourt].filter(Boolean).join(", ")
		: null;
	const fullAddress = [company.street, company.city, company.zip].filter(Boolean).join(", ");

	return (
		<OverviewCard icon={Building2} title="Základní údaje">
			<dl className="flex flex-col gap-3 text-sm">
				<DataRow label="IČO" value={company.ico} mono />
				{company.dic && <DataRow label="DIČ" value={company.dic} mono />}
				{company.legalForm && <DataRow label="Právní forma" value={company.legalForm} />}
				{company.foundingDate && (
					<DataRow label="Datum vzniku" value={formatDate(company.foundingDate)} />
				)}
				{company.dissolutionDate && (
					<DataRow label="Datum zániku" value={formatDate(company.dissolutionDate)} />
				)}
				{company.registeredCapital != null && (
					<DataRow
						label="Základní kapitál"
						value={formatAmountWithCurrency(
							company.registeredCapital,
							company.registeredCapitalCurrency,
						)}
						mono
					/>
				)}
				{fileNumber && <DataRow label="Spisová značka" value={fileNumber} />}
				{company.zpusobJednani && <DataRow label="Způsob jednání" value={company.zpusobJednani} />}
				{company.pocetClenu != null && (
					<DataRow label="Počet členů orgánu" value={String(company.pocetClenu)} />
				)}
				{company.dataBoxId && <DataRow label="Datová schránka" value={company.dataBoxId} mono />}
				{company.employeeLabel && (
					<DataRow label="Počet zaměstnanců" value={company.employeeLabel} />
				)}
			</dl>

			{/* Sídlo */}
			<div className="mt-6">
				<div className="flex items-center gap-2 mb-3">
					<MapPin className="h-4 w-4 text-primary-600" />
					<h3 className="text-sm font-semibold">Sídlo</h3>
				</div>
				{fullAddress ? (
					<div className="flex flex-col gap-2 text-sm">
						<p>{fullAddress}</p>
						<div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
							{company.district && (
								<span className="text-muted-foreground">Okres: {company.district}</span>
							)}
							{company.region &&
								(regionSlug ? (
									<Link
										to={`/directory/kraj/${regionSlug}`}
										className="text-primary hover:underline"
									>
										{company.region}
									</Link>
								) : (
									<span className="text-muted-foreground">{company.region}</span>
								))}
							<a
								href={mapyCzUrl(fullAddress)}
								target="_blank"
								rel="noopener noreferrer"
								className="inline-flex items-center gap-1 text-primary hover:underline"
							>
								Zobrazit na mapě
								<ExternalLink className="h-3.5 w-3.5" />
							</a>
						</div>
					</div>
				) : (
					<p className="text-sm text-muted-foreground">Adresa sídla není k dispozici</p>
				)}
			</div>
		</OverviewCard>
	);
}
